import { Box, Text } from 'ink';
import wrapAnsi from 'wrap-ansi';
import stringWidth from 'string-width';
import { DEFAULT_TERMINAL_WIDTH } from '../../constants';
import { forceEllipsis, truncateToWidth } from '../../messages/tools/truncate';

/** Upper bound on the label column, however long the longest visible label is. */
export const DROPDOWN_LABEL_MAX_WIDTH = 30;

/** Trailing gap reserved after the longest visible label, before the description starts. */
const DROPDOWN_LABEL_GAP = 2;

/** However narrow the terminal gets, the description column never shrinks below this. */
const DROPDOWN_MIN_DESCRIPTION_WIDTH = 8;

/** Descriptions longer than this wrap once more, then get cut off with an ellipsis. */
export const DROPDOWN_DESCRIPTION_MAX_LINES = 2;

/** Clamps labelWidth so it can't exceed columns and starve the description. */
function effectiveLabelWidth(labelWidth: number, columns: number): number {
  return Math.max(1, Math.min(labelWidth, columns - DROPDOWN_MIN_DESCRIPTION_WIDTH));
}

function descriptionWidthFor(columns: number, labelWidth: number): number {
  return Math.max(1, columns - effectiveLabelWidth(labelWidth, columns));
}

export interface DropdownItem {
  id: string;
  label: string;
  description?: string;
  enabled: boolean;
  disabledReason?: string;
  /** Text to substitute for the current word at cursor when this item is selected. */
  replaceWith: string;
  /**
   * When true and the host provides an onSubmit handler, selecting this item
   * via Enter will also submit the form. Tab always inserts only, regardless
   * of this flag.
   *
   * Note: This is a per-item property rather than a Dropdown-level setting
   * because different providers may have different submission behaviors.
   * For example, slash commands should submit immediately on Enter, while
   * file context items should only insert into the input.
   */
  submitAfterSelect?: boolean;
}

export interface DropdownItemProps {
  item: DropdownItem;
  isSelected: boolean;
  /** Terminal width, used to wrap/truncate the description. Defaults to a safe fallback. */
  columns?: number;
  /** Label column width — see {@link computeLabelWidth}. Defaults to the max. */
  labelWidth?: number;
}

/** Widest description-bearing label plus a gap, capped at {@link DROPDOWN_LABEL_MAX_WIDTH} so one outlier can't starve every description. */
export function computeLabelWidth(items: DropdownItem[]): number {
  const longest = items
    .filter((item) => item.description)
    .reduce((max, item) => Math.max(max, stringWidth(item.label)), 0);
  return Math.min(DROPDOWN_LABEL_MAX_WIDTH, longest + DROPDOWN_LABEL_GAP);
}

/** A command name is an identifier, not prose — cut it off rather than wrap it. */
export const truncateLabel = truncateToWidth;

/**
 * Wraps `description` to `width` and, if it would need more than
 * {@link DROPDOWN_DESCRIPTION_MAX_LINES} lines, cuts it off with an ellipsis
 * instead of letting it grow arbitrarily tall.
 */
export function truncateDescription(
  description: string,
  width: number,
  maxLines: number = DROPDOWN_DESCRIPTION_MAX_LINES,
): string {
  const w = Math.max(1, width);
  let lines = wrapAnsi(description, w, { hard: false, trim: false }).split('\n');
  // Fall back to a hard wrap if an unbreakable token (e.g. a URL) overflowed.
  if (lines.some((line) => stringWidth(line) > w)) {
    lines = wrapAnsi(description, w, { hard: true, trim: false }).split('\n');
  }
  if (lines.length <= maxLines) return lines.join('\n');

  const kept = lines.slice(0, maxLines);
  const lastIndex = maxLines - 1;
  kept[lastIndex] = forceEllipsis(kept[lastIndex].trimEnd(), w);
  return kept.join('\n');
}

/**
 * Number of terminal rows this item will render as, given the row's total
 * available width. Used to give {@link computeScrollWindow} an accurate
 * per-item row budget instead of assuming every item is one line tall.
 */
export function measureDropdownItemRows(
  item: DropdownItem,
  columns: number,
  labelWidth: number = DROPDOWN_LABEL_MAX_WIDTH,
): number {
  if (!item.description) return 1;

  return truncateDescription(item.description, descriptionWidthFor(columns, labelWidth)).split('\n')
    .length;
}

export function DropdownItemComponent({
  item,
  isSelected,
  columns = DEFAULT_TERMINAL_WIDTH,
  labelWidth = DROPDOWN_LABEL_MAX_WIDTH,
}: DropdownItemProps) {
  const isDisabled = !item.enabled;
  const clampedLabelWidth = effectiveLabelWidth(labelWidth, columns);
  const description =
    item.description &&
    truncateDescription(item.description, descriptionWidthFor(columns, labelWidth));
  // Reserve the gap so a truncated label doesn't butt into the description; truncate description-less labels too, so they stay the single row measureDropdownItemRows budgets.
  const label = item.description
    ? truncateLabel(item.label, Math.max(1, clampedLabelWidth - DROPDOWN_LABEL_GAP))
    : truncateLabel(item.label, columns);

  return (
    <Box>
      <Box width={item.description ? clampedLabelWidth : undefined} flexShrink={0}>
        <Text inverse={isSelected} dimColor={isDisabled}>
          {label}
        </Text>
      </Box>
      {description && (
        <Box flexGrow={1}>
          <Text dimColor>{description}</Text>
        </Box>
      )}
      {isDisabled && item.disabledReason && (
        <Box>
          <Text dimColor> {item.disabledReason}</Text>
        </Box>
      )}
    </Box>
  );
}
